import { useCallback } from 'react';

import { useFileUpload } from 'api/requests/file';
import { FileTypeName } from 'api/types/entity';
import { useField } from 'libs/form/fields/useField';
import { useFormContext } from 'libs/form/useFormContext';
import { v4 as uuidV4 } from 'uuid';

import { LocalFileEntity } from '../../types';

interface Props {
  maxDocumentCount?: number;
  name: string;
}

type FileFieldType = LocalFileEntity & { loading?: boolean };

export const useFilesUploadFeature = ({ maxDocumentCount, name }: Props) => {
  const { field, helpers, meta } = useField<FileFieldType[]>(name);

  const { setSubmitDisabled } = useFormContext();

  const { upload } = useFileUpload();

  const { isError } = meta;

  const files = field.value;

  const onAddFiles = useCallback(
    async (acceptedFiles: File[]) => {
      setSubmitDisabled(true);

      if (maxDocumentCount) {
        const addCount = maxDocumentCount - (files?.length ?? 0);
        acceptedFiles = acceptedFiles.slice(0, addCount);
      }

      const newValues = [...(files ?? [])];

      await Promise.all(
        acceptedFiles.map(async (uploadFile) => {
          const id = uuidV4();
          newValues.push({
            id,
            name: uploadFile.name,
            createdAt: new Date(),
            size: uploadFile.size,
            loading: true,
            type: FileTypeName.Unknown,
          });
          helpers.setValue(newValues);

          const serverId = await upload(uploadFile).catch();

          const index = newValues.findIndex((f) => f.id === id);
          newValues[index].id = serverId;
          newValues[index].loading = false;

          helpers.setValue([...newValues]);
        })
      );
      helpers.setError('');
      setSubmitDisabled(false);
    },
    [files, helpers, maxDocumentCount, setSubmitDisabled, upload]
  );

  const onRemoveFile = useCallback(
    (id: string) => {
      const newValues = files.filter((f) => f.id !== id);
      helpers.setValue([...newValues]);
      helpers.setError('');
    },
    [files, helpers]
  );

  return { files: field.value, isError, onRemoveFile, onAddFiles };
};
